<script setup lang="ts">
import { computed, defineAsyncComponent, markRaw, onBeforeMount, ref } from 'vue'

import { isDark } from '@/utils/color'
import BetaEditorElementAction from '@/components/beta-editor/BetaEditorElementAction.vue'
import BetaEditorElementBeforeAfterImage from '@/components/beta-editor/elements/BetaEditorElementBeforeAfterImage.vue'
import BetaEditorElementButton from '@/components/beta-editor/elements/BetaEditorElementButton.vue'
import BetaEditorElementComments from '@/components/beta-editor/elements/BetaEditorElementComments.vue'
import BetaEditorElementContainer from '@/components/beta-editor/elements/BetaEditorElementContainer.vue'
import BetaEditorElementDefault from '@/components/beta-editor/elements/BetaEditorElementDefault.vue'
import BetaEditorElementFaq from '@/components/beta-editor/elements/BetaEditorElementFaq.vue'
import BetaEditorElementForm from '@/components/beta-editor/elements/BetaEditorElementForm.vue'
import BetaEditorElementGallery from '@/components/beta-editor/elements/BetaEditorElementGallery.vue'
import BetaEditorElementImage from '@/components/beta-editor/elements/BetaEditorElementImage.vue'
import BetaEditorElementRow from '@/components/beta-editor/elements/BetaEditorElementRow.vue'
import BetaEditorElementScript from '@/components/beta-editor/elements/BetaEditorElementScript.vue'
import BetaEditorElementSubpages from '@/components/beta-editor/elements/BetaEditorElementSubpages.vue'
import BetaEditorElementText from '@/components/beta-editor/elements/BetaEditorElementText.vue'
import BetaEditorElementTitle from '@/components/beta-editor/elements/BetaEditorElementTitle.vue'
import BetaEditorElementVideo from '@/components/beta-editor/elements/BetaEditorElementVideo.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorPageStore } from '@/stores/editor/page'
import { BETA_EDITOR_SITE_CLIENT_BREAKPOINT } from '@/utils/editor-beta-preview'

const componentLoaders: Record<string, () => Promise<any>> = {
    title: async () => ({ default: BetaEditorElementTitle }),
    text: async () => ({ default: BetaEditorElementText }),
    gallery: async () => ({ default: BetaEditorElementGallery }),
    image: async () => ({ default: BetaEditorElementImage }),
    video: async () => ({ default: BetaEditorElementVideo }),
    button: async () => ({ default: BetaEditorElementButton }),
    comments: async () => ({ default: BetaEditorElementComments }),
    faq: async () => ({ default: BetaEditorElementFaq }),
    form: async () => ({ default: BetaEditorElementForm }),
    row: async () => ({ default: BetaEditorElementRow }),
    container: async () => ({ default: BetaEditorElementContainer }),
    script: async () => ({ default: BetaEditorElementScript }),
    beforeafterimage: async () => ({ default: BetaEditorElementBeforeAfterImage }),
    'before-after-image': async () => ({ default: BetaEditorElementBeforeAfterImage }),
    before_after_image: async () => ({ default: BetaEditorElementBeforeAfterImage }),
    subpages: async () => ({ default: BetaEditorElementSubpages }),
    default: async () => ({ default: BetaEditorElementDefault }),
}

const props = defineProps<{
    element: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    sectionHasBackgroundImage?: boolean
    isHome?: boolean
    isAbout?: boolean
    isEditable?: boolean
    isChild?: boolean
    elementParent?: any
    elementParentIndex?: number
    schema?: any
    headingStyle?: Record<string, string>
}>()

const betaStore = useEditorBetaStore()
const editorPageStore = useEditorPageStore()
const component = ref<any>(null)

const normalizeGridUnits = (value: unknown) => {
    const parsedValue = Number(value)

    if (!Number.isFinite(parsedValue)) return 12

    return Math.min(12, Math.max(1, parsedValue))
}

const isFull = computed(() => props.element.grid === 99)
const isMobile = computed(() => {
    if (betaStore.isMobilePreview) return true
    if (typeof window === 'undefined') return false

    return window.innerWidth < BETA_EDITOR_SITE_CLIENT_BREAKPOINT
})

const gridSizeDesktopPorcent = computed(() => {
    if (isFull.value) return
    if ((props.isHome || !props.isEditable) && !props.isAbout) return '100%'

    return `${(normalizeGridUnits(props.element.grid) / 12) * 100}%`
})

const gridSizeMobilePorcent = computed(() => {
    if (props.element.grid === 99) return
    if (props.isHome || !props.isEditable) return '100%'

    return `${(normalizeGridUnits(props.element.gridMobile ?? props.element.grid) / 12) * 100}%`
})

const dynamicWidth = computed(() => {
    if (isFull.value) return

    return `calc(${isMobile.value ? gridSizeMobilePorcent.value : gridSizeDesktopPorcent.value})`
})
const rootStyle = computed(() => ({
    width: dynamicWidth.value ?? '100%',
    maxWidth: '100%',
    minWidth: '0',
}))

const isDarkMode = computed(() => isDark(props.sectionBackgroundColor))
const previewSubpagesContent = computed(() => {
    if (props.element.frameType !== 'subpages') return null

    const sectionFrameId = editorPageStore.activePage?.content?.[props.sectionIndex]?.frameId

    return betaStore.getImportedSubpagesPreviewContent(
        sectionFrameId,
        props.element.frameId ?? props.element.temporaryId,
    )
})
const elementForRender = computed(() => {
    if (previewSubpagesContent.value == null) return props.element

    return {
        ...props.element,
        frameContent: previewSubpagesContent.value,
    }
})
const elementId = computed(() => betaStore.getElementId(elementForRender.value))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const normalizedFrameType = computed(() => String(elementForRender.value?.frameType ?? '').toLowerCase())
const componentStyle = computed(() => {
    const imageWidthIsUnlocked = normalizedFrameType.value === 'image'
        && !elementForRender.value?.extra?.style_width_lock
        && elementForRender.value?.extra?.style?.width

    if (imageWidthIsUnlocked) return undefined

    return { width: '100%' }
})
const hasResizeAction = computed(() => !['row', 'script', 'comments', 'faq', 'subpages'].includes(normalizedFrameType.value))
const isProtectedHomeFirstGallery = computed(() => editorPageStore.isProtectedHomeFirstGallery(elementForRender.value))
const hasCloneAction = computed(() => !isProtectedHomeFirstGallery.value)
const hasDeleteAction = computed(() => !isProtectedHomeFirstGallery.value)
const canSelectElement = computed(() => props.isEditable !== false || ['gallery', 'form'].includes(normalizedFrameType.value))

const onSelectElement = (event?: PointerEvent) => {
    if (!canSelectElement.value) return

    const currentTarget = event?.currentTarget as HTMLElement | null
    const closestElementRoot = (event?.target as HTMLElement | null)?.closest?.('[data-beta-element-root]')

    if (currentTarget && closestElementRoot && closestElementRoot !== currentTarget) {
        return
    }

    betaStore.setSelectedElementBySchema(props.schema, elementForRender.value)

    if (elementForRender.value.frameType === 'text') {
        betaStore.setEditingElement(elementForRender.value)
    } else {
        betaStore.clearEditingElement()
        betaStore.clearActiveTextEditor()
    }
}

onBeforeMount(async () => {
    component.value = await markRaw(defineAsyncComponent(async () => {
        try {
            const frameType = String(elementForRender.value.frameType ?? '').toLowerCase()
            const loader = componentLoaders[frameType] || componentLoaders.default
            const module = await loader()

            if (!module.default) throw new Error('Componente não foi importado corretamente.')

            return module.default
        } catch (error) {
            console.error('Erro durante o carregamento assíncrono do componente:', error)

            return await componentLoaders.default()
        }
    }))
})
</script>

<template>
    <div
        v-if="component"
        :id="elementId"
        data-beta-element-root
        class="beta-element-root"
        :data-active="isActive"
        :data-frame-type="normalizedFrameType"
        :style="rootStyle"
        @pointerdown.capture="onSelectElement"
    >
        <beta-editor-element-action
            v-if="isActive && props.isEditable !== false"
            :element="elementForRender"
            :schema="schema"
            :has-resize="hasResizeAction"
            :has-clone="hasCloneAction"
            :has-delete="hasDeleteAction"
        />

        <component
            :is="component"
            class="beta-element-root__content"
            :data-active="isActive"
            :style="componentStyle"
            :element="elementForRender"
            :extra="elementForRender.extra"
            :element-index="elementIndex"
            :section-index="sectionIndex"
            :is-dark-mode="isDarkMode"
            :is-full="isFull"
            :section-background-color="sectionBackgroundColor"
            :section-has-background-image="sectionHasBackgroundImage"
            :is-child="isChild"
            :element-parent="elementParent"
            :element-parent-index="elementParentIndex"
            :is-editable="isEditable"
            :schema="schema"
            :heading-style="headingStyle"
        />
    </div>
</template>

<style scoped>
.beta-element-root {
    @apply relative min-w-0 max-w-full;
    box-sizing: border-box;
    overflow-wrap: anywhere;
    transition:
        width 1000ms ease-in-out,
        max-width 1000ms ease-in-out,
        flex-basis 1000ms ease-in-out,
        transform 1000ms ease-in-out,
        opacity 1000ms ease-in-out;
    will-change: width, transform, opacity;
}

.beta-element-root[data-frame-type='image'] {
    @apply flex justify-center;
}

.beta-element-root__content {
    @apply block min-w-0 max-w-full;
    box-sizing: border-box;
    transition:
        border-color 1000ms ease-in-out,
        box-shadow 1000ms ease-in-out,
        background-color 1000ms ease-in-out,
        color 1000ms ease-in-out,
        padding 1000ms ease-in-out,
        margin 1000ms ease-in-out;
}

.beta-element-root[data-frame-type='container'] {
    @apply flex;
}

.beta-element-root[data-frame-type='container'] > .beta-element-root__content {
    @apply flex flex-1;
}

.beta-element-root :deep(*),
.beta-element-root :deep(*::before),
.beta-element-root :deep(*::after) {
    box-sizing: border-box;
}

.beta-element-root :deep(img),
.beta-element-root :deep(video),
.beta-element-root :deep(iframe),
.beta-element-root :deep(canvas) {
    max-width: 100%;
}
</style>
